import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import * as History from "../../../../../common-deprecated/History";
import {
    type FinishFinanceRateFetchType,
    type FinishPSLSFetchType,
    type StartFinanceRateFetchType,
    type StartInsuranceRateFetchType,
    type StartPSLSFetchType,
    type UpdateQuoteConfigActionType,
    type ClearInsuranceRateActionType,
    type ClearFinanceRateActionType,
    type SetFinanceProductType,
    resetFinanceRates,
    FINISH_FINANCE_RATE_FETCH,
    FINISH_PSLS_FETCH,
    finishFinanceRateFetch,
    finishInsuranceRateFetch,
    finishPSLSFetch,
    START_FINANCE_RATE_FETCH,
    START_INSURANCE_RATE_FETCH,
    START_PSLS_FETCH,
    startFinanceRateFetch,
    startInsuranceRateFetch,
    startPSLSFetch,
    UPDATE_QUOTE_CONFIG,
    updateQuoteConfig as updateQuoteConfigAction,
    CLEAR_INSURANCE_RATE,
    setInsurancePersonalized,
    CLEAR_FINANCE_RATE,
    SET_FINANCE_PRODUCT_TYPE,
    setFinanceProductType,
} from "../actions/FinanceInsuranceActions";

import { startConfigSave } from "../actions/SaveActions";
import CarDataConsolidator from "../../../../shared-logic/utils/classes/CarDataConsolidator";
import Debug from "../../../../../common-deprecated/Debug";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import {
    RESET_CALCULATOR,
    setFinanceOption,
    type ResetCalculatorType,
    type PrepareCalculatorType,
    PREPARE_CALCULATOR,
} from "../../../../../common-deprecated/redux/actions/UtilActions";
import {
    getExternalConfigurationObject,
    getFinanceExternalConfigurationObject,
    pslsFinanceResponseHasNoQuotes,
} from "../../utils/typedUtils";
import { sendJsonToSalesman, EnvironmentEnum } from "../../../../../common-deprecated/utils";
import { postalPublish } from "../../../../../common-deprecated/utils/postalUtils";
import { fetchInsuranceRate, fetchMonthlyRate } from "../../utils/FinanceInsuranceRate";
import type { BuildAndBuyLogicType } from "../store";
import {
    type PostalChannelType,
    type PostalTopicType,
    POSTAL_CHANNEL_FQS,
    POSTAL_TOPIC_FQS_QUOTE_SET,
    SALESMAN_QUOTE_CONFIG,
} from "../../../../../common-deprecated/constants";
import { fetchExternalPriceFunctions } from "../actions/ExternalPriceActions";
import {
    getPersonalFinanceInsuranceEnabled,
    isFinanceCompareQuotesSet,
    showBnbInsurance,
    showBnbMonthlyRates,
} from "../../utils/settings/bnbFinanceSettingUtils";
import { showTradeIn } from "../../utils/settings/bnbSettingUtils";
import type { PSLSDataServiceResponseType } from "../../../../shared-logic/types/PSLSDataType";
import { addNotification } from "../../../../../common-deprecated/redux/actions/NotificationActions";
import { getBnbLabel } from "../../utils/labels";
import { getModelToken, isMaster } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { formatPSLSObject } from "../../../../shared-logic/utils/formatters/formatPSLSObject";
import { getTradeInNotificationConfig } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtils";
import { setTradeInNotification } from "../../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { getExternalConfigOptionsAccessoryIdsDeprecated } from "../../../../shared-logic/utils/typedUtils";
import { SESSION_STORAGE_FINANCE_QUOTES_KEY } from "../../utils/constants";
import { COMPONENT_PDF } from "../../../../../shared-logic/server/components";
import { checkAndSetFinanceProductTradeInCompatibility } from "../../utils/basketUtils";
import { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

const updateQuoteConfig = (createLogic as BuildAndBuyLogicType<UpdateQuoteConfigActionType>)({
    type: UPDATE_QUOTE_CONFIG,

    // eslint-disable-next-line consistent-return
    validate({ getState, action }, allow, reject) {
        const { finance, car, navigation, commonSettings, bnbSettings } = getState();
        const { quotes, info } = finance;

        // Quote configs are only relevant in front-end and on summary/customize steps.
        if (
            !navigation.step ||
            ![NavStep.Summary, NavStep.Services, NavStep.Customize].includes(navigation.step) ||
            !DOMUtils.canUseDOM
        ) {
            return (reject as () => void)();
        }

        // Get the necessary settings to determine if finance/insurance quotes should be updated.
        const { personalFinanceEnabled, personalInsuranceEnabled } = getPersonalFinanceInsuranceEnabled(bnbSettings);

        const updateFinanceRates =
            personalFinanceEnabled &&
            showBnbMonthlyRates(commonSettings, bnbSettings, car.model.internalCode, navigation.step, car);
        const updateInsuranceRates = personalInsuranceEnabled && showBnbInsurance(commonSettings, bnbSettings, car);

        const financeData = action.data?.finance;
        const insuranceData = action.data?.insurance;
        if (financeData || insuranceData) {
            // If the quotes are the same; do nothing.
            if (
                (financeData === quotes.finance && insuranceData === quotes.insurance) ||
                (!updateFinanceRates && !updateInsuranceRates) ||
                (!updateFinanceRates && insuranceData === quotes.insurance) ||
                (!updateInsuranceRates && financeData === quotes.finance)
            ) {
                // Quotes might be the same but productName might differ, in this case still allow the action.
                if (action.data?.productName ? info.productName === action.data.productName : true)
                    return (reject as () => void)();
            }

            action.quotes = { ...quotes };
            if (financeData && updateFinanceRates) action.quotes.finance = financeData;
            if (insuranceData && updateInsuranceRates) action.quotes.insurance = insuranceData;
        } else {
            action.quotes = { finance: null, insurance: null };
        }

        action.updateFinanceRates = updateFinanceRates;
        action.updateInsuranceRates = updateInsuranceRates;

        allow(action);
    },

    process({ getState, action }, dispatch, done) {
        const state = getState();
        const { bnbSettings, commonSettings, utils } = state;

        if (isMaster(commonSettings)) {
            sendJsonToSalesman({
                action: SALESMAN_QUOTE_CONFIG,
                data: updateQuoteConfigAction(action.data),
            });
        }

        // If updateQuoteConfig is triggered and returns a finance quote...
        // We need to remove the financeProductType from the Redux store because this would overwrite the financeData.
        if (action.quotes?.finance) {
            dispatch(setFinanceProductType(""));
        }

        // If externalPriceCalculation is enabled and we are setting the quotes for the first time
        // we'll load the external price function after the quotes have been set.
        // In this case monthly rate gets fetched in the calculateTotalPrice, triggered when the external price function is correctly set.
        if (action.loadExternalPriceFunction) {
            dispatch(fetchExternalPriceFunctions());
        } else {
            // Reload the monthly rates as per usual.
            if (action.updateFinanceRates) {
                const [financeNotificationTitle, financeNotificationDescription] = getBnbLabel(
                    { commonSettings, bnbSettings },
                    ["financeNotificationTitle", "financeNotificationDescription"],
                );

                dispatch(startFinanceRateFetch(FinanceUpdateTrigger.NewQuote));
                if (
                    action.quotes?.finance &&
                    commonSettings.query.financeOption === "monthly" &&
                    (bnbSettings.showFinanceInsuranceInPage || bnbSettings.enableFinanceCompare)
                ) {
                    // If finance updates & is valid, financeOption should be monthly
                    if (utils.financeOption !== "monthly") {
                        dispatch(setFinanceOption("monthly"));
                    }

                    dispatch(
                        addNotification([
                            {
                                title: financeNotificationTitle,
                                description: financeNotificationDescription,
                            },
                        ]),
                    );
                }
            }

            // Always trigger the config save so T1.ctx is populated with most recent data
            // But after finance has been validated and set to monthly
            dispatch(startConfigSave());

            if (action.updateInsuranceRates) {
                const [insuranceNotificationTitle, insuranceNotificationDescription] = getBnbLabel(
                    { commonSettings, bnbSettings },
                    ["insuranceNotificationTitle", "insuranceNotificationDescription"],
                );

                dispatch(startInsuranceRateFetch());
                if (
                    action.quotes?.insurance &&
                    (bnbSettings.showFinanceInsuranceInPage || bnbSettings.enableFinanceCompare)
                ) {
                    dispatch(
                        addNotification([
                            {
                                title: insuranceNotificationTitle,
                                description: insuranceNotificationDescription,
                            },
                        ]),
                    );
                }
            }
        }

        done();
    },
});

const startPSLSFetchLogic = (createLogic as BuildAndBuyLogicType<StartPSLSFetchType>)({
    type: START_PSLS_FETCH,
    latest: true,

    validate({ action }, allow, reject) {
        // The basket calls the start fetch rate method and checks if the price has changed in the calculatePriceLogic
        // This initially also gets called once offcourse
        if (!DOMUtils.canUseDOM && !action.fetchAndParseJson) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ action, getState }, dispatch, done) {
        const { car, basket, items, bnbSettings, commonSettings } = getState();
        // Create the URL which returns the PSLS data based on current configuration/basket
        const { brand, country, language } = commonSettings;
        const { pslsGateway } = bnbSettings;
        const { colour, upholstery, packs } = basket;
        const { options, accessories } = getExternalConfigOptionsAccessoryIdsDeprecated(basket, items, true, true);
        const modelToken = getModelToken(commonSettings, car.model.id, true);
        if (!modelToken || !pslsGateway) {
            if (!modelToken) Debug.error("No model tokens available to load the PSLS service!");
            if (!pslsGateway) Debug.error("No PSLS gateway set");
            dispatch(finishPSLSFetch(null, true, action.parentAction));
            return done();
        }
        const packsString =
            packs
                .filter((pack) => !pack.standard)
                .map((pack) => pack.shortId)
                .join(",") || "-";
        const optionsString = options.length ? options.join(",") : "-";
        const accessoriesString = accessories.length ? accessories.join(",") : "-";

        const basePath = `${pslsGateway}/brand/${brand}/country/${country}/language/${language}/configuration/product-token/${
            modelToken || ""
        }/car/${car.id}`;
        const url = `${basePath}/exteriorcolour/${colour.internalCode}/upholstery/${upholstery.internalCode}/packs/${packsString}/options/${optionsString}/accessories/${accessoriesString}/productspecification`;

        // Create the URL to load the PSLS data based on the configuration object
        try {
            let result: PSLSDataServiceResponseType;
            if (action.fetchAndParseJson) {
                result = await action.fetchAndParseJson(url, "PSLS", undefined, "GET");
            } else {
                const fetchResult = await fetch(url);
                result = await fetchResult.json();
            }

            if (!result.Successful) {
                Debug.error("Error returned from API: ", {
                    brand,
                    country,
                    language,
                    carId: car.id,
                    modelId: car.model.id,
                    result,
                });
                throw new Error("Error returned from API");
            }
            dispatch(finishPSLSFetch(formatPSLSObject(result), !result.Successful, action.parentAction));
        } catch (e) {
            Debug.error("Failed to fetch psls data", e);
            dispatch(finishPSLSFetch(null, true, action.parentAction));
        }

        return done();
    },
});

const finishPSLSFetchLogic = (createLogic as BuildAndBuyLogicType<FinishPSLSFetchType>)({
    type: FINISH_PSLS_FETCH,
    process({ action }, dispatch, done) {
        // If there is a parentAction present, call it again to let it know the PSLS data has been loaded
        if (action.parentAction) {
            // Indicate that in this action flow, the psls is loaded
            action.parentAction.pslsLoaded = true;
            dispatch(action.parentAction);
        }
        done();
    },
});

const startFinanceFetch = (createLogic as BuildAndBuyLogicType<StartFinanceRateFetchType>)({
    type: START_FINANCE_RATE_FETCH,
    latest: true,
    validate({ action, getState }, allow, reject) {
        const { psls, bnbSettings } = getState();
        // The basket calls the start fetch rate method and checks if the price has changed in the calculatePriceLogic
        // This initially also gets called once of course
        if (
            (!DOMUtils.canUseDOM && !action.fetchAndParseJson) ||
            (bnbSettings.addPSLSToFinanceConfigurationObject && psls.fetching)
        ) {
            (reject as () => void)();
        } else if (bnbSettings.addPSLSToFinanceConfigurationObject && !action.pslsLoaded) {
            reject(startPSLSFetch(action, action.fetchAndParseJson));
        } else {
            allow(action);
        }
    },

    async process({ action, getState }, dispatch, done) {
        const {
            car,
            basket,
            items,
            finance,
            commonSettings,
            bnbSettings,
            psls,
            tradeInAemExternalData,
            externalPrice,
        } = getState();
        const allExtras = CarDataConsolidator.getAllRelevantBasketExtras(basket, items);
        const config = getFinanceExternalConfigurationObject(
            commonSettings,
            car,
            basket,
            items,
            tradeInAemExternalData,
            finance.quotes,
            externalPrice,
            finance.financeProductType,
            finance.info.productName,
            bnbSettings.disableTradeInForFinanceProducts,
        );
        const selectedItems = [basket.wheel.id, basket.upholstery.id, basket.colour.id]
            .concat(allExtras.filter((extra) => extra.type === "option").map((extra) => extra.id))
            .concat(allExtras.filter((extra) => extra.type === "accessory").map((extra) => extra.id))
            .concat(basket.packs.map((pack) => pack.id));
        const unSelectedItems = CarDataConsolidator.getUnselectedItemIds(basket, items).concat(
            items.winterWheels.map((winterWheel) => winterWheel.id),
        );

        // If data is available, add it to the configuration object
        if (psls.data) config.PSLS = psls.data;

        try {
            const result = await fetchMonthlyRate(
                commonSettings,
                bnbSettings,
                config,
                selectedItems,
                unSelectedItems,
                action.fetchAndParseJson,
            );

            dispatch(finishFinanceRateFetch(result, action.trigger));
        } catch (e) {
            dispatch(resetFinanceRates());
            Debug.error("Failed to fetch monthly rate", e);
        }
        done();
    },
});

const finishFinanceFetch = (createLogic as BuildAndBuyLogicType<FinishFinanceRateFetchType>)({
    type: FINISH_FINANCE_RATE_FETCH,

    transform({ getState, action }, next) {
        const { finance } = getState();
        const { unselectedItemPrices, selectedItemPrices } = action.result;

        const newRates = { ...finance.itemRates };

        unselectedItemPrices.forEach((rate) => {
            newRates[rate.id] = rate.value;
        });
        selectedItemPrices.forEach((rate) => {
            newRates[rate.id] = rate.value;
        });
        action.rates = newRates;
        action.previousDownPayment = finance.quoteInfo.DOWNPAYMENT ? Number(finance.quoteInfo.DOWNPAYMENT.value) : 0;

        next(action);
    },

    async process({ getState, action }, dispatch, done) {
        const { finance, bnbSettings, commonSettings, tradeInAemExternalData, utils } = getState();
        const { quoteInfo } = action.result;
        const { msgCode } = action.result.info;

        // If there is no valid response from the monthly rate service and addPSLSToFinanceConfigurationObject is set to true, toggle to cash!
        if (
            pslsFinanceResponseHasNoQuotes(finance, bnbSettings) ||
            !isFinanceCompareQuotesSet(bnbSettings.enableFinanceCompare, finance.quotes.finance)
        ) {
            Debug.warn("ccGetMonthlyPayment returned with empty response, hiding monthly rate tab.");
            dispatch(setFinanceOption("cash"));
            // Trade-in finance integration: Check if a notification should be shown.
        } else if (showTradeIn(bnbSettings, commonSettings, utils.financeOption, finance.info.productName)) {
            let show = false;
            let notificationType;
            let isError;

            if (msgCode) {
                // Check if there is a difference in deposit.
                const nextDownPayment = quoteInfo!.DOWNPAYMENT ? Number(quoteInfo!.DOWNPAYMENT.value) : 0;
                const depositDiff = nextDownPayment - action.previousDownPayment!;

                ({ notificationType, isError, show } = getTradeInNotificationConfig(
                    msgCode,
                    action.trigger,
                    depositDiff,
                    !(commonSettings.environment === EnvironmentEnum.Production),
                    tradeInAemExternalData.notification.monthly.notificationType,
                    tradeInAemExternalData.notification.monthly.show,
                    bnbSettings.allowedTradeinProduct,
                    finance.info.productName,
                ));
            }
            dispatch(setTradeInNotification("monthly", show, notificationType, isError));
        }

        if (tradeInAemExternalData.data.vehicleValue) {
            checkAndSetFinanceProductTradeInCompatibility(
                tradeInAemExternalData.data,
                !!tradeInAemExternalData.incompatible,
                utils.financeOption,
                finance.info.productName,
                bnbSettings,
                dispatch,
            );
        }
        done();
    },
});

const startInsuranceFetch = (createLogic as BuildAndBuyLogicType<StartInsuranceRateFetchType>)({
    type: START_INSURANCE_RATE_FETCH,

    validate({ action, getState }, allow, reject) {
        const { psls, bnbSettings, finance, commonSettings } = getState();
        // The basket calls the start fetch rate method and checks if the price has changed in the calculatePriceLogic
        // This initially also gets called once offcourse

        const isPdf = commonSettings.component === COMPONENT_PDF;

        if (
            (!DOMUtils.canUseDOM && !action.fetchAndParseJson) ||
            (bnbSettings.addPSLSToInsuranceConfigurationObject && psls.fetching) ||
            (!bnbSettings.requireDefaultInsurance && !finance.quotes.insurance) ||
            // Make sure fqs is initialized before fetching insurance rates, only applies when personal finance is enabled
            (!isPdf && bnbSettings.personalInsuranceEnabled && !finance.fqsInitialized)
        ) {
            (reject as () => void)();
        } else if (bnbSettings.addPSLSToInsuranceConfigurationObject && !action.pslsLoaded) {
            reject(startPSLSFetch(action, action.fetchAndParseJson));
        } else {
            allow(action);
        }
    },

    async process({ action, getState }, dispatch, done) {
        const { car, basket, items, finance, bnbSettings, commonSettings, tradeInAemExternalData, psls } = getState();

        const config = getExternalConfigurationObject(
            commonSettings,
            car,
            basket,
            items,
            tradeInAemExternalData,
            true,
            finance.quotes,
        );

        // If data is available, add it to the configuration object
        if (psls.data) config.PSLS = psls.data;

        try {
            const result = await fetchInsuranceRate(bnbSettings, config, action.fetchAndParseJson);
            dispatch(finishInsuranceRateFetch(result));
        } catch (e) {
            Debug.error("Failed to fetch insurance rate", e);
        }

        done();
    },
});

const prepareCalculatorLogic = (createLogic as BuildAndBuyLogicType<PrepareCalculatorType>)({
    type: PREPARE_CALCULATOR,

    validate({ getState, action }, allow, reject) {
        const { executeSave, loadPSLS } = action;
        const { save, psls } = getState();
        if (executeSave && !save.saved) {
            reject(startConfigSave(action));
        } else if (loadPSLS && !action.pslsLoaded && !psls.data && !psls.fetching) {
            reject(startPSLSFetch(action));
        } else allow(action);
    },
});

const resetCalculatorLogic = (createLogic as BuildAndBuyLogicType<ResetCalculatorType>)({
    type: RESET_CALCULATOR,

    process({ getState, action }, dispatch, done) {
        const { utils } = getState();
        if (utils.financeOption === "cash" && action.calculatorType === "finance")
            dispatch(setFinanceOption("monthly"));
        done();
    },
});

const clearInsuranceLogic = (createLogic as BuildAndBuyLogicType<ClearInsuranceRateActionType>)({
    type: CLEAR_INSURANCE_RATE,

    async process(_, dispatch, done) {
        // fetch default finance quotes after the insurance state has been reset
        window.requestAnimationFrame(() => {
            postalPublish({
                channel: POSTAL_CHANNEL_FQS,
                topic: POSTAL_TOPIC_FQS_QUOTE_SET,
                data: {
                    insurance: null, // setting empty quote id fetches default quotes
                },
            });
        });

        dispatch(setInsurancePersonalized(false));
        return done();
    },
});

const clearFinanceLogic = (createLogic as BuildAndBuyLogicType<ClearFinanceRateActionType>)({
    type: CLEAR_FINANCE_RATE,

    async process(_, dispatch, done) {
        // fetch default finance quotes after the insurance state has been reset
        window.requestAnimationFrame(() => {
            postalPublish({
                channel: (window.fqs?.contract.events.channel || "") as PostalChannelType,
                topic: (window.fqs?.contract.events.setQuotes || "") as PostalTopicType,
                data: {
                    finance: null, // setting empty quote id fetches default quotes
                },
            });
        });

        dispatch(setInsurancePersonalized(false));
        return done();
    },
});

const updateFinanceProductType = (createLogic as BuildAndBuyLogicType<SetFinanceProductType>)({
    type: SET_FINANCE_PRODUCT_TYPE,

    process({ action }, _, done) {
        const storage = sessionStorage.getItem(SESSION_STORAGE_FINANCE_QUOTES_KEY);

        // When there is a financeProductType availaible and there is already a finance quote in the storage...
        // We remove that quote from the sessions storage. This way the custom finance data is overwritten by the given productType query.
        if (!!action.financeProductType && !!storage) {
            sessionStorage.removeItem(SESSION_STORAGE_FINANCE_QUOTES_KEY);
        }
        // If the financeProductType state is empty, we remove the query from the url.
        else if (!action.financeProductType) {
            History.replaceQueryParams({ financeProductType: null });
        }

        done();
    },
});

export default [
    updateQuoteConfig,
    startPSLSFetchLogic,
    finishPSLSFetchLogic,
    startFinanceFetch,
    finishFinanceFetch,
    startInsuranceFetch,
    prepareCalculatorLogic,
    resetCalculatorLogic,
    clearInsuranceLogic,
    clearFinanceLogic,
    updateFinanceProductType,
];

export const financeInsuranceServerLogic = [
    startFinanceFetch,
    finishFinanceFetch,
    startPSLSFetchLogic,
    finishPSLSFetchLogic,
    startInsuranceFetch,
];
